import { useEffect, useState } from 'react';
import { api, ApiError } from '@/lib/api.js';

/*
 * /api/history/season/<season> returns everything six tabs need in one
 * payload: history (player season totals), matrix (points per gameweek),
 * team_history (team totals and xG), dd_hauls and the two double-digit
 * aggregations. Fetching it once here avoids six requests for the same
 * document when a user moves between tabs.
 *
 * Responses are cached per season so switching back is instant.
 */
const cache = new Map();

export function useSeasonData(season) {
	const [data, setData] = useState(() => cache.get(season) ?? null);
	const [status, setStatus] = useState(() => (cache.has(season) ? 'ready' : 'loading'));
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!season) return undefined;
		if (cache.has(season)) {
			setData(cache.get(season));
			setStatus('ready');
			return undefined;
		}

		let cancelled = false;
		setStatus('loading');
		setError(null);
		api
			.get(`/api/history/season/${season}`)
			.then((payload) => {
				if (cancelled) return;
				cache.set(season, payload);
				setData(payload);
				setStatus('ready');
			})
			.catch((err) => {
				if (cancelled) return;
				setError(err instanceof ApiError ? err.message : 'Could not load that season.');
				setStatus('error');
			});
		return () => {
			cancelled = true;
		};
	}, [season]);

	return { data, status, error };
}
